// stores/wsStore.js
import { Nullable, Timeout } from '@crossbeam/types';

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import usePolling from '@/composables/usePolling';
import appConfig from '@/config';

export type ChatErrorCode = 'unknown' | 'rate_limit' | 'context_overflow';

export type Message = {
  content: string;
  chatSessionId: string;
  messageId?: string;
  messageComplete: boolean;
  isFirst: boolean;
  errorCode: Nullable<ChatErrorCode>;
};

export type SentMessage = {
  content: string;
  chatSessionId: Nullable<string>;
  current_page: {
    url: string;
    params: Record<string, string>;
  } | null;
  image?: string | null;
};

export const useWebSocketStore = defineStore('wsStore', () => {
  const ws = ref<Nullable<WebSocket>>(null);
  const messages = ref<string[]>([]);
  const isConnected = computed(() => {
    return ws.value ? ws.value.readyState === WebSocket.OPEN : false;
  });
  const retryTimeout = ref<Nullable<Timeout>>(null);

  const parsedMessages = computed<Message[]>(() => {
    return messages.value.map((msg) => JSON.parse(msg));
  });

  const chatSessionId = computed(() => {
    // we want to use the last message's chatSessionId in case we start a fresh one
    // due to context limit errors.
    return (
      parsedMessages?.value[parsedMessages?.value.length - 1]?.chatSessionId ||
      null
    );
  });

  function connect() {
    if (ws.value && isConnected.value) return;
    const { currentOrg } = useAuth();

    const orgId = currentOrg.value?.uuid;
    if (!orgId) return;

    const url = `wss://${appConfig.aiChatHost}/ws/?organization_uuid=${orgId}`;

    ws.value = new WebSocket(url);

    ws.value.onopen = () => {
      if (retryTimeout.value) {
        clearTimeout(retryTimeout.value);
        retryTimeout.value = null;
      }
    };

    ws.value.onmessage = (e: { data: string }) => {
      messages.value.push(e.data);
    };

    ws.value.onerror = () => {
      // TODO, error handling logic
    };

    ws.value.onclose = () => {
      pollForConnection();
    };
  }

  async function pollForConnection() {
    const MAX_RETRIES = 30;
    const pollInfo = {
      poller: () => {
        connect();
        return Promise.resolve(); // for TS in usePolling
      },
      interval: 5000,
      shouldComplete: (_?: unknown, calls?: number) => {
        const numCalls = calls || 0;
        return Boolean(numCalls >= MAX_RETRIES || isConnected.value);
      },
    };
    await usePolling(pollInfo)();
  }

  function sendMessage(data: SentMessage) {
    if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify(data));
    }
  }

  function disconnect() {
    if (ws.value) {
      ws.value.close();
      ws.value = null;
    }
    if (retryTimeout.value) {
      clearTimeout(retryTimeout.value);
      retryTimeout.value = null;
    }
  }

  function getMessagesById(id: string) {
    return parsedMessages.value.filter((msg) => msg.messageId === id);
  }

  return {
    ws,
    parsedMessages,
    isConnected,
    retryTimeout,
    getMessagesById,
    connect,
    sendMessage,
    disconnect,
    chatSessionId,
  };
});
